/**
 * Cleans and normalizes speech-to-text output after a recording session ends.
 */
export const cleanSTTText = (text: string): string => {
  if (!text || !text.trim()) return text;

  let cleaned = text;

  cleaned = cleaned.replace(/\s+\./g, ".");
  cleaned = cleaned.replace(/\s+,/g, ",");
  cleaned = cleaned.replace(/\s+\?/g, "?");
  cleaned = cleaned.replace(/\s+!/g, "!");
  cleaned = cleaned.replace(/\s+/g, " ").trim();

  const sentences: Array<{ text: string; punctuation: string }> = [];
  const sentenceEnders = /([.!?]+)/g;
  let lastIndex = 0;
  let match;

  while ((match = sentenceEnders.exec(cleaned)) !== null) {
    const beforePunctuation = cleaned.substring(lastIndex, match.index).trim();
    const punctuation = match[1];
    if (beforePunctuation) {
      sentences.push({ text: beforePunctuation, punctuation });
    }
    lastIndex = match.index + match[0].length;
  }

  const remaining = cleaned.substring(lastIndex).trim();
  if (remaining) {
    sentences.push({ text: remaining, punctuation: "" });
  }

  const mergedSentences: Array<{ text: string; punctuation: string }> = [];
  for (let i = 0; i < sentences.length; i++) {
    const current = sentences[i];
    const wordCount = current.text
      .split(/\s+/)
      .filter((w) => w.length > 0).length;

    if (
      wordCount < 3 &&
      i + 1 < sentences.length &&
      sentences[i + 1].text.length > 0 &&
      /^[a-z]/.test(sentences[i + 1].text)
    ) {
      const next = sentences[i + 1];
      mergedSentences.push({
        text: current.text + " " + next.text,
        punctuation: next.punctuation || current.punctuation,
      });
      i++;
    } else {
      mergedSentences.push(current);
    }
  }

  const processedSentences: Array<{ text: string; punctuation: string }> = [];
  for (const sentence of mergedSentences) {
    let sentenceText = sentence.text.trim();
    if (!sentenceText) continue;

    const firstLetterMatch = sentenceText.match(/[a-zA-Z]/);
    if (firstLetterMatch && firstLetterMatch.index !== undefined) {
      const before = sentenceText.substring(0, firstLetterMatch.index);
      const letter = sentenceText[firstLetterMatch.index];
      const after = sentenceText.substring(firstLetterMatch.index + 1);
      sentenceText = before + letter.toUpperCase() + after;
    }

    const words = sentenceText.split(/(\s+)/);
    const processedWords = words.map((word, wordIndex) => {
      if (wordIndex === 0) return word;
      if (!/^[A-Z][a-z]*$/.test(word)) return word;
      if (word.length === 1) return word.toLowerCase();
      if (word.length <= 2 && wordIndex > 2) {
        return word.toLowerCase();
      }
      return word;
    });
    sentenceText = processedWords.join("");

    processedSentences.push({ text: sentenceText, punctuation: sentence.punctuation });
  }

  const sentenceParts: string[] = [];
  for (let i = 0; i < processedSentences.length; i++) {
    const sentence = processedSentences[i];
    if (!sentence.text) continue;

    if (sentence.punctuation) {
      sentenceParts.push(sentence.text + sentence.punctuation);
    } else if (i < processedSentences.length - 1) {
      sentenceParts.push(sentence.text + ".");
    } else {
      sentenceParts.push(sentence.text);
    }
  }

  const reassembled = sentenceParts
    .map((part, index) => {
      if (index === sentenceParts.length - 1) return part;
      return part + " ";
    })
    .join("")
    .trim();

  let final = reassembled.trim();
  const hadPunctuation = /[.!?]/.test(cleaned);
  if (
    !final.endsWith(".") &&
    !final.endsWith("?") &&
    !final.endsWith("!") &&
    hadPunctuation
  ) {
    final = final + ".";
  }

  return final.trim();
};
